import {
  deriveStoryContextKind,
  storyOfferVariant,
  LISTING_FORMATS,
  type ListingFormat,
  type ListingType,
  type StoryContextKind,
} from "@kraahl/contract"

import type { BadgeSpec } from "./shared"
import { BADGE_PALETTE } from "./shared"

// `ListingFormat` is re-exported so the historical
// `import { ListingFormat } from "@/lib/story-cards"` path (via the barrel
// at `lib/story-cards/index.ts:357 export * from "./types"`) keeps working.
// Canonical decl lives in `packages/contract/src/listing-adt.ts`.
export type { ListingFormat }

export interface BaseStoryPayload {
  imageUrl: string
  title: string
  seller?: string
  listingUrl?: string
  currency?: string
}

export interface BuyNowStoryContext extends BaseStoryPayload {
  kind: "buyNow"
  price: number | string
}

export interface AuctionStoryContext extends BaseStoryPayload {
  kind: "auction"
  startPrice: number | string
  highestBid?: number | string | null
  bidCount?: number | null
  endAt?: string | null
  status?: "scheduled" | "live" | "ended" | "settled" | string | null
}

export interface PreorderStoryContext extends BaseStoryPayload {
  kind: "preorder"
  price: number | string
  deadline?: string | null
  estimatedDelivery?: string | null
  currentOrders?: number | null
  minOrders?: number | null
  maxOrders?: number | null
  status?: "open" | "funded" | "in_production" | "shipped" | string | null
}

export interface OfferStoryContext extends BaseStoryPayload {
  kind: "offer"
  variant: "hybrid" | "only"
  price?: number | string | null
}

export interface ScheduledStoryContext extends BaseStoryPayload {
  kind: "scheduled"
  publishAt: string
  price?: number | string | null
}

export type StoryCardContext =
  | BuyNowStoryContext
  | AuctionStoryContext
  | PreorderStoryContext
  | OfferStoryContext
  | ScheduledStoryContext

export interface ListingShareMeta {
  listingFormat?: ListingFormat | string | null
  isPreorder?: boolean | null
  scheduledPublishAt?: string | null
  auction?: {
    startPrice?: number | null
    highestBid?: number | null
    bidCount?: number | null
    endAt?: string | null
    status?: string | null
  } | null
  preorder?: {
    deadline?: string | null
    estimatedDelivery?: string | null
    currentOrders?: number | null
    minOrders?: number | null
    maxOrders?: number | null
    status?: string | null
  } | null
}

const CANONICAL_FORMATS: ReadonlySet<ListingFormat> = new Set(LISTING_FORMATS)

/**
 * Narrow a raw `meta.listingFormat` string into the canonical `ListingFormat`
 * union. Story-card meta arrives from many call-sites — most pass a known
 * literal, some pass `null`/`undefined`/an unknown legacy value. Unknown
 * values collapse to `'fixed_price'` (the dominant prod default) so
 * `deriveStoryContextKind`'s exhaustive switch stays type-safe.
 */
function narrowListingFormat(raw: ListingFormat | string | null | undefined): ListingFormat {
  if (raw && CANONICAL_FORMATS.has(raw as ListingFormat)) {
    return raw as ListingFormat
  }
  if (raw) {
    console.warn(
      `[story-cards] unknown listing_format ${JSON.stringify(raw)} — collapsing to "fixed_price". A new format was added to the DB without updating the canonical LISTING_FORMATS tuple in @kraahl/contract.`,
    )
  }
  return "fixed_price"
}

function isFutureDate(iso: string | null | undefined): boolean {
  if (!iso) return false
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return false
  return d.getTime() > Date.now()
}

/**
 * Resolve a story-card context from the share-card payload + raw listing
 * meta. The narrative `kind` discriminator is selected by the canonical
 * `deriveStoryContextKind` helper from `@kraahl/contract` (Phase 7 of
 * `shippo-listing-discriminator`) — no parallel if/else on raw fields. The
 * per-kind shape (auction.endAt, preorder.deadline, etc.) stays here
 * because it's presentation-layer-only and not encoded by the canonical
 * `Listing` ADT.
 */
export function resolveStoryContext(
  base: BaseStoryPayload & { price?: number | string | null },
  meta: ListingShareMeta | undefined,
): StoryCardContext {
  // 1. Canonical kind selection — same helper the item-card builder uses
  //    (different output union, same discriminator inputs). The `auction`
  //    branch also fires when `meta.auction` is present without an explicit
  //    `listingFormat='auction'` (legacy share-payload shape) — fold that
  //    into the discriminator before calling the helper.
  const format = meta?.listingFormat
    ? narrowListingFormat(meta.listingFormat)
    : meta?.auction
      ? "auction"
      : "fixed_price"
  const type: ListingType = meta?.isPreorder ? "preorder" : "standard"
  const kind: StoryContextKind = deriveStoryContextKind(
    { format, type },
    { isScheduledDrop: isFutureDate(meta?.scheduledPublishAt) },
  )

  // 2. Per-kind shape assembly — presentation detail outside the canonical
  //    ADT (story-card-specific fields like deadline / endAt / variant).
  switch (kind) {
    case "scheduled": {
      // Guard the non-null narrowing — kind='scheduled' is reachable only
      // when isFutureDate(meta.scheduledPublishAt) returned true, which
      // requires the string to be present.
      const publishAt = meta?.scheduledPublishAt ?? ""
      return { ...base, kind: "scheduled", publishAt, price: base.price ?? null }
    }
    case "auction": {
      const auction = meta?.auction ?? {}
      return {
        ...base,
        kind: "auction",
        startPrice: auction.startPrice ?? (typeof base.price === "number" ? base.price : Number(base.price ?? 0)),
        highestBid: auction.highestBid ?? null,
        bidCount: auction.bidCount ?? 0,
        endAt: auction.endAt ?? null,
        status: auction.status ?? null,
      }
    }
    case "preorder": {
      const preorder = meta?.preorder ?? {}
      return {
        ...base,
        kind: "preorder",
        price: base.price ?? 0,
        deadline: preorder.deadline ?? null,
        estimatedDelivery: preorder.estimatedDelivery ?? null,
        currentOrders: preorder.currentOrders ?? null,
        minOrders: preorder.minOrders ?? null,
        maxOrders: preorder.maxOrders ?? null,
        status: preorder.status ?? null,
      }
    }
    case "offer": {
      // hybrid (`fixed_price_with_offers`) carries the price; only
      // (`offers_only`) does not — single SoT lives in `storyOfferVariant`.
      const variant = storyOfferVariant(format)
      if (variant === "only") {
        return { ...base, kind: "offer", variant: "only", price: null }
      }
      // hybrid (or any non-only offer-enabled format) — carry the price.
      return { ...base, kind: "offer", variant: "hybrid", price: base.price ?? null }
    }
    case "buyNow":
      return { ...base, kind: "buyNow", price: base.price ?? 0 }
  }
}

export function badgeForContext(ctx: StoryCardContext): BadgeSpec | null {
  switch (ctx.kind) {
    case "auction":
      return { label: ctx.status === "ended" || ctx.status === "settled" ? "Auction" : "Live auction", ...BADGE_PALETTE.auction }
    case "preorder":
      return { label: "Pre-order", ...BADGE_PALETTE.preorder }
    case "offer":
      return ctx.variant === "only"
        ? { label: "Offers only", ...BADGE_PALETTE.offerOnly }
        : { label: "Or make an offer", ...BADGE_PALETTE.offerHybrid }
    case "scheduled":
      return { label: "Dropping soon", ...BADGE_PALETTE.scheduled }
    case "buyNow":
    default:
      return null
  }
}
